import { Container, Graphics, LINE_CAP, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"

// The shared chrome of the HUD's round gauges — the pre-wave COUNTDOWN DIAL (countdown_timer.ts)
// and the WAVE BADGE that takes its slot once the wave lands (wave_indicator.ts).
//
// The two are meant to read as one object handing over: the countdown drains to zero, vanishes, and
// the badge grows into the same spot already full. That only works if the face, groove and rim are
// literally identical, which is what this module guarantees. What separates them is the GAUGE —
// palette, direction, what it measures — and that stays in each widget.
//
// Everything here is drawn with plain fills and strokes. No filters: a soft edge is a fatter, fainter
// copy of the same path, because the low-end hardware this game targets cannot spare a filter pass.
//
// The look is the BUILD BOARD's (build_board.ts) — cut timber, bronze rim, corner rivets — because
// these dials hang over the very camp those controls build. What they are deliberately NOT is a
// stopwatch: a thin ring of even hour marks on a flat grey disc is a wall clock, and a wall clock is
// the one thing a siege warning must not look like. Hence a forged bezel with rivets, quarter notches
// instead of hours, and a face that heats from within rather than a plate that turns red.

// Bezel and face, in the build board's timber-and-bronze family.
export const DIAL_BEZEL_COLOR = 0x3d2e20 // build_board TIMBER
export const DIAL_BEZEL_LIGHT = 0xd9b483 // build_board RIM_LIGHT — the lit upper-left of the band
export const DIAL_RIVET_COLOR = 0x8e7b58 // build_board RIM
export const DIAL_FACE_COLOR = 0x15120d // build_board FACE
export const DIAL_FACE_ALPHA = 0.9
export const DIAL_GROOVE_COLOR = 0x000000
export const DIAL_GROOVE_ALPHA = 0.45

// Width of the forged band around the face. It is PAINTED OUTSIDE the face radius, so it widens the
// dial's footprint — which is why every caller measures with dialSize/dialBottomExtent rather than
// assuming radius*2.
export const DIAL_BEZEL_WIDTH = 5

export interface DialMetrics {
    radius: number
    strokeWidth: number
}

// Radius the gauge arc is stroked along: half a stroke in from the dial's edge, so the stroke sits
// wholly inside the face rather than straddling it.
export const dialArcRadius = ({ radius, strokeWidth }: DialMetrics): number => radius - strokeWidth / 2

// Radius of the dark face behind the gauge. Half a stroke PROUD of the arc's outer edge, which is
// what gives the gauge a rim to run inside.
export const dialFaceRadius = ({ radius, strokeWidth }: DialMetrics): number => radius + strokeWidth / 2

// Radius of the bezel's outer edge — the dial's actual painted edge.
export const dialBezelOuter = (metrics: DialMetrics): number => dialFaceRadius(metrics) + DIAL_BEZEL_WIDTH

// Outer painted size of a dial — what a caller positions against. Deliberately computed rather than
// measured: `width` on a live dial moves with its glow, its gauge and its entrance scale, so laying
// out against it makes the HUD's geometry depend on the frame it was read on.
export const dialSize = (metrics: DialMetrics): number => dialBezelOuter(metrics) * 2

export interface DialFaceProps extends DialMetrics {
    faceColor?: number
    faceAlpha?: number
}

/**
 * Draw a dial's static chrome into `g`, which is cleared first.
 *
 * Drawn ONCE per widget (the gauge on top of it is what moves), so it can afford the passes that
 * give it depth: a dropped silhouette, a forged band lit from the upper left, the recessed face, the
 * groove the gauge runs in, four quarter notches and four rivets.
 */
export const drawDialFace = (g: Graphics, props: DialFaceProps) => {
    const arcRadius = dialArcRadius(props)
    const faceRadius = dialFaceRadius(props)
    const outer = dialBezelOuter(props)
    const band = (faceRadius + outer) / 2

    g.clear()

    // Sits ON the camp rather than in it.
    g.beginFill(0x000000, 0.34)
    g.drawCircle(0, 3, outer)
    g.endFill()

    g.beginFill(DIAL_BEZEL_COLOR, 1)
    g.drawCircle(0, 0, outer)
    g.endFill()

    // The band is lit from the upper left and shadowed opposite, which is what makes it read as
    // forged metal rather than a printed ring. Two arcs on the band's own centreline.
    const sweep = (from: number, to: number, color: number, alpha: number) => {
        g.lineStyle({ width: DIAL_BEZEL_WIDTH * 0.62, color, alpha, cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(from) * band, Math.sin(from) * band)
        g.arc(0, 0, band, from, to)
    }
    sweep(Math.PI * 1.04, Math.PI * 1.88, DIAL_BEZEL_LIGHT, 0.5)
    sweep(Math.PI * 0.08, Math.PI * 0.86, 0x000000, 0.42)

    g.lineStyle(0)
    g.beginFill(props.faceColor ?? DIAL_FACE_COLOR, props.faceAlpha ?? DIAL_FACE_ALPHA)
    g.drawCircle(0, 0, faceRadius)
    g.endFill()

    g.lineStyle({ width: props.strokeWidth, color: DIAL_GROOVE_COLOR, alpha: DIAL_GROOVE_ALPHA, cap: LINE_CAP.ROUND })
    g.drawCircle(0, 0, arcRadius)

    // Four quarter notches cut through the band — a pressure gauge's marks, not a clock's hours.
    // Twelve evenly spaced ticks is the single strongest "wall clock" tell there is.
    for (let quarter = 0; quarter < 4; quarter++) {
        const angle = -Math.PI / 2 + (quarter / 4) * Math.PI * 2
        g.lineStyle({ width: 2.4, color: 0x000000, alpha: 0.55, cap: LINE_CAP.ROUND })
        g.moveTo(Math.cos(angle) * (faceRadius - 1), Math.sin(angle) * (faceRadius - 1))
        g.lineTo(Math.cos(angle) * (outer - 0.5), Math.sin(angle) * (outer - 0.5))
    }

    // Rivets on the diagonals, where they cannot sit on a notch.
    g.lineStyle(0)
    for (let corner = 0; corner < 4; corner++) {
        const angle = -Math.PI / 4 + (corner / 4) * Math.PI * 2
        const x = Math.cos(angle) * band
        const y = Math.sin(angle) * band
        g.beginFill(0x000000, 0.5)
        g.drawCircle(x, y + 0.5, 2.1)
        g.endFill()
        g.beginFill(DIAL_RIVET_COLOR, 0.95)
        g.drawCircle(x, y, 1.7)
        g.endFill()
        g.beginFill(DIAL_BEZEL_LIGHT, 0.7)
        g.drawCircle(x - 0.5, y - 0.6, 0.7)
        g.endFill()
    }

    // A hairline inside the face edge, so the face reads as recessed into the band.
    g.lineStyle({ width: 1, color: 0x000000, alpha: 0.55 })
    g.drawCircle(0, 0, faceRadius - 0.5)
}

/**
 * Warm the face from within. Draws into `g` (NOT cleared — it shares the effects layer with the
 * ripple), as a few concentric low-alpha discs: the cheap way to a radial glow with no filter.
 *
 * `heat` is 0..1. This replaces a flat red plate, which turned the dial into a warning light; a
 * furnace brightening behind the numerals is the same information and belongs to the game.
 */
export const drawDialHeat = (g: Graphics, metrics: DialMetrics, color: number, heat: number) => {
    if (!(heat > 0)) {
        return
    }
    const faceRadius = dialFaceRadius(metrics)
    // Four stacked discs, each smaller and stronger than the last, so the centre is the brightest
    // part. The alphas are deliberately heavy: the first pass at this was so faint that the dial at
    // three seconds was DIMMER than the dial at twenty-four, which is the escalation backwards.
    for (let ring = 0; ring < 4; ring++) {
        const spread = 1 - ring * 0.23
        g.beginFill(color, 0.1 * heat * (1 + ring * 0.5))
        g.drawCircle(0, 0, (faceRadius - 1) * spread)
        g.endFill()
    }
}

export interface DialGaugeProps extends DialMetrics {
    // 0..1 of the ring to fill, swept CLOCKWISE from twelve o'clock.
    progress: number
    color: number
    // Width added to the fatter, fainter copy drawn behind the arc, and its alpha. This is the
    // "glow": a second stroke, not a filter.
    haloWidth?: number
    haloAlpha?: number
    // Colour of the arc itself, when it should differ from the halo's (a flash washing it white).
    arcColor?: number
    // Bright bead at the draining end, so the eye can find how far round the gauge has got.
    bead?: boolean
}

/** Draw a gauge arc into `g`, which is cleared first. A non-positive progress leaves it empty. */
export const drawDialGauge = (g: Graphics, props: DialGaugeProps) => {
    g.clear()
    if (!(props.progress > 0)) {
        return
    }

    const arcRadius = dialArcRadius(props)
    const startAngle = -Math.PI / 2
    const endAngle = startAngle + Math.min(1, props.progress) * Math.PI * 2
    const startX = Math.cos(startAngle) * arcRadius
    const startY = Math.sin(startAngle) * arcRadius

    if (props.haloWidth && props.haloAlpha) {
        g.lineStyle({
            width: props.strokeWidth + props.haloWidth,
            color: props.color,
            alpha: props.haloAlpha,
            cap: LINE_CAP.ROUND,
        })
        g.moveTo(startX, startY)
        g.arc(0, 0, arcRadius, startAngle, endAngle)
    }

    g.lineStyle({
        width: props.strokeWidth,
        color: props.arcColor ?? props.color,
        alpha: 1,
        cap: LINE_CAP.ROUND,
    })
    g.moveTo(startX, startY)
    g.arc(0, 0, arcRadius, startAngle, endAngle)

    // The draining end is an EMBER, not a white pin: a soft halo around an opaque hot heart. Same
    // recipe as the camp's fire — the glow has to sit under something solid or it reads as dust.
    if (props.bead !== false) {
        const x = Math.cos(endAngle) * arcRadius
        const y = Math.sin(endAngle) * arcRadius
        g.lineStyle(0)
        g.beginFill(props.color, 0.45)
        g.drawCircle(x, y, props.strokeWidth * 0.72)
        g.endFill()
        g.beginFill(0xfff2d0, 0.95)
        g.drawCircle(x, y, props.strokeWidth * 0.3)
        g.endFill()
    }
}

/** Mix two packed RGB colours. `factor` is clamped, so callers can hand it raw easing output. */
export const interpolateColor = (from: number, to: number, factor: number): number => {
    const f = Math.max(0, Math.min(1, factor))
    const r = Math.round(((from >> 16) & 0xff) + (((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * f)
    const g = Math.round(((from >> 8) & 0xff) + (((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * f)
    const b = Math.round((from & 0xff) + ((to & 0xff) - (from & 0xff)) * f)
    return (r << 16) | (g << 8) | b
}

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), 3)

// ===== the three lines inside a dial ==================================================

// Both dials carry the same stack in the same places: which WAVE this is, the number, and what the
// number means. The top line is the reason they are shared — "WAVE 3" on the countdown is the wave
// that is COMING and "WAVE 3" on the badge is that same wave being fought, so across the hand-over
// it is the one thing that does not move at all.
//
// It used to hang UNDER the ring in a pill. That said the same thing twice on the countdown (a pill
// reading "WAVE 3" below a number captioned "INCOMING"), and it spent the slot directly under the
// dial, which is the only other place in that column anything can go.
//
// The line is drawn as TWO runs — a small muted "WAVE" and a bigger bright number — so that it stays
// legible as the camp works through a twenty-wave table. One flat uppercase run grows with every
// digit and starts competing with the numeral below it; splitting it gives the digits their own
// weight and keeps "WAVE 20" no louder than "WAVE 2".
export const DIAL_LABEL_COLOR = 0xffedc7 // build_board INK
export const DIAL_LABEL_WORD_COLOR = 0xa08c6c // build_board MUTED
export const DIAL_LABEL_WORD_SPACING = 3 // between the word and its number
// pixi adds a letterSpacing advance after the LAST character too, so `Text.width` on the word
// overstates its ink by exactly this much. Left in, it both widens the gap and pushes the pair off
// the dial's axis — which is visible on a dial this size.
export const DIAL_LABEL_LETTER_SPACING = 1.2

// Measured from the dial's CENTRE. What a line has to clear is not the face but the GAUGE GROOVE,
// whose inner edge is 42.5 on the HUD's radius-50 dials: every line sits INSIDE the ring, and a line
// is a box whose top CORNERS reach further out than its midpoint does.
//
// These y's come from measuring the real strings in the browser, not from estimating them — the
// estimates were wrong by enough to matter. "WAVE 20", the widest the camp's table asks for, is
// 52.8 wide, and at y -22 its corners clear the groove by 2.2px; at the -26 this first shipped with
// they CUT the ring. The caption, at 23, is why the countdown has none: "INCOMING" measures 68 and
// overlapped the ring by 2.5px at every size worth reading. "LEFT" measures 30 and clears by 9.4.
export const DIAL_LABEL_Y = -22
export const DIAL_VALUE_Y = 0
export const DIAL_CAPTION_Y = 23

/**
 * How far below a dial's CENTRE it paints. Lets a caller keep the thing on screen, and stack
 * something under it, without reaching into either widget's layout.
 */
export const dialBottomExtent = (metrics: DialMetrics): number => dialBezelOuter(metrics)

/**
 * Where each run of a wave line sits so the PAIR is centred on the dial's axis — which is what keeps
 * "WAVE 20" from hanging off to one side of a line laid out for "WAVE 2".
 *
 * Pure, and tested with real measured widths, because `Text.width` under jsdom is a stub that
 * returns the same few pixels whatever the string says (see game-client-test-harness): asserting the
 * centring on a rendered label would pass no matter how wrong the arithmetic was.
 */
export const dialLabelRunX = (wordWidth: number, digitsWidth: number): { word: number; digits: number } => {
    const total = wordWidth + DIAL_LABEL_WORD_SPACING + digitsWidth
    const word = -total / 2
    return { word, digits: word + wordWidth + DIAL_LABEL_WORD_SPACING }
}

/**
 * The "WAVE n" line both dials wear, as one widget so the two cannot drift apart.
 *
 * Parents itself at DIAL_LABEL_Y and centres its own two runs, so an owner only has to add it and
 * say which wave. Zero takes it down: a bare timer has no wave to name.
 */
export class DialLabel extends Container {
    private word: Text
    private digits: Text
    private wave = -1

    constructor() {
        super()

        this.word = new TextRenderer()
            .color(DIAL_LABEL_WORD_COLOR)
            .fontSize(TEXT_ROLES.overline.fontSize)
            .bold()
            .render("WAVE") as Text
        this.word.anchor.set(0, 0.5)
        // Letterspaced so four small capitals still read as a word rather than a smudge.
        this.word.style.letterSpacing = DIAL_LABEL_LETTER_SPACING

        this.digits = new TextRenderer().color(DIAL_LABEL_COLOR).fontSize(12).bold().render("1") as Text
        this.digits.anchor.set(0, 0.5)

        this.addChild(this.word, this.digits)
        this.y = DIAL_LABEL_Y
        this.setWave(1)
    }

    public setWave(wave: number) {
        this.visible = wave > 0
        if (!this.visible || wave === this.wave) {
            return
        }
        this.wave = wave
        this.digits.text = String(wave)
        const at = dialLabelRunX(this.word.width - DIAL_LABEL_LETTER_SPACING, this.digits.width)
        this.word.x = at.word
        this.digits.x = at.digits
    }

    /** Test seam: what the line currently reads, as one string. */
    public readsAs(): string {
        return `${this.word.text} ${this.digits.text}`
    }
}
